/**
 * Utilitários para lidar com chamadas à API
 */

// Import mantido para futuras evoluções onde o util possa decidir base URL dinamicamente
// Mantido para futuras evoluções; removido o uso direto para evitar lint de unused
// import { getApiBaseUrl } from '../config/api';
import { AUTH_SEED } from '../config/api';
import { limparAutenticacao, montarValorAutorizacao } from './authToken';
// {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: USAR FETCH COM TIMEOUT SEM AbortSignal.timeout}
import { fetchComTimeout } from './fetchTimeout';
// {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: USAR FETCH COM TIMEOUT SEM AbortSignal.timeout}

/**
 * Verifica se a resposta da API é JSON válido e faz o parse
 * @param response Objeto Response do fetch
 * @returns Dados JSON parseados
 * @throws Error se a resposta não for JSON válido
 */
export async function parseJsonResponse<T = any>(response: Response): Promise<T> {
  // Primeiro, verifica se é erro 403 Forbidden (token expirado/sessão inválida)
  if (response.status === 403) {
    limparAutenticacao(true);
    throw new Error('Sessão expirada. Por favor, faça login novamente.');
  }
  
  // {|INICIO| 08/04/2026 às 14:30 - TIPAGEM: AJUSTAR DEFAULT DO parseJsonResponse PARA EVITAR unknown EM STRICT MODE}
  // Verifica se a resposta é realmente JSON antes de fazer o parse
  const contentType = response.headers.get('content-type');
  
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    console.error('Resposta não é JSON:', {
      status: response.status,
      statusText: response.statusText,
      contentType,
      url: response.url,
      text: text.substring(0, 500) + (text.length > 500 ? '...' : '')
    });
    
    // Se a resposta contém texto indicando token expirado, trata como sessão expirada
    if (text.includes('403') || text.includes('Forbidden') || text.includes('Token expired') || text.includes('token expirado')) {
      limparAutenticacao(true);
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    
    // Se a resposta é HTML e contém indicadores de erro do servidor
    if (contentType?.includes('text/html')) {
      if (text.includes('404') || text.includes('Not Found')) {
        throw new Error('Endpoint da API não encontrado. Verifique a configuração do servidor.');
      }
      if (text.includes('500') || text.includes('Internal Server Error')) {
        throw new Error('Erro interno do servidor. Tente novamente mais tarde.');
      }
      if (text.includes('403') || text.includes('Forbidden')) {
        limparAutenticacao(true);
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }
      // Se é uma página HTML genérica, provavelmente é um problema de roteamento
      if (text.includes('<html') || text.includes('<!DOCTYPE')) {
        throw new Error('A API está retornando uma página web ao invés de dados JSON. Verifique a configuração do proxy ou do servidor.');
      }
    }
    
    throw new Error('O servidor retornou uma resposta inválida. Por favor, tente novamente mais tarde.');
  }
  
  try {
    return await response.json() as T;
  } catch (jsonError) {
    console.error('Erro ao fazer parse do JSON:', jsonError);
    throw new Error('Resposta JSON inválida do servidor.');
  }
  // {|FIM| 08/04/2026 às 14:30 - TIPAGEM: AJUSTAR DEFAULT DO parseJsonResponse PARA EVITAR unknown EM STRICT MODE}
}

/**
 * Verifica se a URL da API está acessível
 * @param baseUrl URL base da API
 * @returns true se a API estiver acessível, false caso contrário
 */
export async function ping(baseUrl: string): Promise<boolean> {
  try {
      // In development, try to ping a known endpoint that exists
      const apiUrl = `${baseUrl}/reserva_online_checkout_rest_service/ping`;
      
      // {|INICIO| 23/07/2026 às 10:00 - AUTH: PRIORIZAR BEARER NO PING DE DISPONIBILIDADE DA API}
      // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      const response = await fetchComTimeout(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': montarValorAutorizacao(AUTH_SEED),
          'Accept': 'application/json'
        },
      }, 3000);
      // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      // {|FIM| 23/07/2026 às 10:00 - AUTH: PRIORIZAR BEARER NO PING DE DISPONIBILIDADE DA API}
      
      // Se conseguiu fazer a requisição (mesmo que retorne erro), a API está disponível
      return response.status !== 0; // 0 indica falha de rede/CORS

  } catch (error) {
    console.error('Erro ao verificar disponibilidade da API:', error);
    return false;
  }
}
